import { FC, useCallback, useMemo, useState } from "react";

import { PreclassCheckResultType } from "../../../../../shared/types/preclass-check";

import { SystemCheckResultsData } from "api/api-data-types";
import { serverGetSystemCheckResults } from "api/api-server-data";
import { PreclassCheckMessages } from "components/av/preclass-check-messages";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { usePromiseEffect } from "store/store-hooks";

type SystemChecksTableProps = {
  data: SystemCheckResultsData[];
};

const SystemChecksTable: FC<SystemChecksTableProps> = ({ data }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString();
  };
  const getTestErrorMessage = (testResults: PreclassCheckResultType) => {
    try {
      const message = PreclassCheckMessages.getHighestPriorityMessageFromResults(testResults);
      return message.message || "";
    } catch {
      return "";
    }
  };
  return (
    <ScrollableTableContainer className="mb-0 max-h-[330px] overflow-y-auto">
      <Table>
        <THead>
          <tr>
            <TH>{t("user_info.name")}</TH>
            <TH>{t("user_info.email")}</TH>
            <TH>{t("user_info.created_date")}</TH>
            <TH>{t("av.system_check.report.check_date")}</TH>
            <TH>{t("av.system_check.report.IP")}</TH>
            <TH>{t("av.system_check.report.browser")}</TH>
            <TH>{t("av.system_check.report.OS")}</TH>
            <TH>{t("av.system_check.report.outcome")}</TH>
            <TH>{t("av.system_check.report.error")}</TH>
          </tr>
        </THead>
        <tbody>
          {data.map((row) => (
            <TR key={row.created_at + row.user_email + row.ip}>
              <TD>{row.user_full_name}</TD>
              <TD>{row.user_email}</TD>
              <TD>{formatDate(row.user_created_at)}</TD>
              <TD>{formatDate(row.created_at)}</TD>
              <TD>{row.ip}</TD>
              <TD>{row.browser || "-"}</TD>
              <TD>{row.os || "-"}</TD>
              <TD>{row.check_end_result}</TD>
              <TD>{getTestErrorMessage(row.test_results)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};

export const SystemChecksReport: FC = () => {
  const [onlyShowFailed, setOnlyShowFailed] = useState(false);

  type DateRangeOption = {
    id: string;
    title: string;
    value: number;
  };

  const DATE_RANGE_OPTIONS: DateRangeOption[] = [
    { id: "none", title: t("common.date_range.select_time_period"), value: 0 },
    { id: "7", title: t("common.date_range.last_7_days"), value: 7 },
    { id: "30", title: t("common.date_range.last_30_days"), value: 30 },
    { id: "all", title: t("common.date_range.all_time"), value: -1 },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(
    DATE_RANGE_OPTIONS.find((option) => option.id === "none"),
  );

  // Calculate date range based on selected option
  const getStartDate = useCallback(() => {
    if (selectedDateRange.value === -1) {
      return "2020-01-01T00:00:00Z";
    }
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - selectedDateRange.value);
    return startDate.toISOString();
  }, [selectedDateRange.value]);

  const { data, isLoading, error } = usePromiseEffect(async () => {
    const startDate = getStartDate();

    if (!selectedDateRange || selectedDateRange.id === "none") {
      return [];
    }
    return serverGetSystemCheckResults(startDate);
  }, [selectedDateRange, getStartDate]);

  const filteredResults = useMemo(() => {
    return !data ? [] : data.filter((row) => !onlyShowFailed || row.check_end_result === "failed");
  }, [data, onlyShowFailed]);

  return (
    <div className="system-checks-report-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("av.system_check.report.title")}</h2>
      </div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div>
          <DropdownMenu
            className="mds-input w-60"
            eventName="SystemChecksReport / Time period selected"
            eventPropertyKey="timePeriod"
            items={DATE_RANGE_OPTIONS}
            openMenuEventName="SystemChecksReport / Open date range menu"
            selectedValue={selectedDateRange.id}
            size="s"
            title={t("common.date_range.select_time_period")}
            onSelectionChange={(item) => setSelectedDateRange(item)}
          />
        </div>
        <div className="flex h-10 items-center">
          <Checkbox
            checked={onlyShowFailed}
            className="mr-2 mt-[6px]"
            id="only-show-failed"
            onChange={(checked) => setOnlyShowFailed(checked)}
          >
            {t("av.system_check.report.only_failed_tests")}
          </Checkbox>
        </div>
      </div>
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading_ellipsis")}</div>
      )}
      {filteredResults && <SystemChecksTable data={filteredResults} />}
      {filteredResults && filteredResults.length === 0 && !error && (
        <div className="mt-8 text-center text-black-tint-40">
          {t("av.system_check.report.select_date_to_view_data")}
        </div>
      )}
    </div>
  );
};
